import { useQuery } from '@tanstack/react-query';
import { useTimeout } from 'common-hooks';
import { useRum } from 'common-rum';
import { getRandomPollInterval, HttpError } from 'common-utils';
import {
  CheckState,
  DocumentId,
  DocumentProcess,
  PeerPilotCheck,
} from 'preflight-core';
import { useEffect, useState } from 'react';

import { getDelayedDocumentProcess } from '../resources/delayed-document-process';
import { Checks, Journal } from '../resources/journal';

export interface subSectionWiseResultInter {
  groupSubsection: string;
  checkResult: string;
  description: string;
}

export type ChecklistState =
  | {
      name: 'loading';
      checks: Checks;
      peerPilotChecks?: PeerPilotCheck[];
      selectedModules?: string[];
      selectedModuleResults?: {
        DETECT_AI?: {
          label?: string;
          score?: number;
          scoreLevel?: string;
        };
        PLAGIARISM_CHECK?: {
          label?: string;
          score?: number;
          scoreLevel?: string;
        };
        PEER_PILOT?: {
          label?: string;
          score?: number;
          scoreLevel?: string;
        };
        IMAGE_CHECK?: {
          label?: string;
          score?: number;
          scoreLevel?: string;
        };
      };
      fileName?: string;
      articleType?: {
        id?: number;
        name?: string;
        master?: boolean;
      };
      fileUploadDate?: string;
      manuscriptId?: string;
    }
  | {
      name: 'error';
      checks: Checks;
      selectedModules?: string[];
      selectedModuleResults?: {
        DETECT_AI?: {
          label?: string;
          score?: number;
          scoreLevel?: string;
        };
        PLAGIARISM_CHECK?: {
          label?: string;
          score?: number;
          scoreLevel?: string;
        };
        PEER_PILOT?: {
          label?: string;
          score?: number;
          scoreLevel?: string;
        };
        IMAGE_CHECK?: {
          label?: string;
          score?: number;
          scoreLevel?: string;
        };
      };
      fileName?: string;
      articleType?: {
        id?: number;
        name?: string;
        master?: boolean;
      };
      fileUploadDate?: string;
      manuscriptId?: string;
      retry: () => void;
    }
  | {
      name: 'done';
      checks: Checks;
      summaryResult: DocumentProcess['summaryResult'];
      languageSuggestions: number;
      peerPilotChecks?: PeerPilotCheck[];
      sectionWiseResult?: Record<string, string>;
      subSectionWiseResult?: subSectionWiseResultInter[];
      integrityCheckSummary?: string;
      languageAccessibilityScore?: string;
      languageSummaryResult?: string;
      technicalSummaryResult?: string;
      articleType?: {
        id?: number;
        name?: string;
        master?: boolean;
      };
      selectedModules?: string[];
      selectedModuleResults?: {
        DETECT_AI?: {
          label?: string;
          score?: number;
          scoreLevel?: string;
        };
        PLAGIARISM_CHECK?: {
          label?: string;
          score?: number;
          scoreLevel?: string;
        };
        PEER_PILOT?: {
          label?: string;
          score?: number;
          scoreLevel?: string;
        };
        IMAGE_CHECK?: {
          label?: string;
          score?: number;
          scoreLevel?: string;
        };
      };
      smartScreenModuleResult?: string;
      detectAiModuleResult?: {
        label?: string;
        score?: number;
        scoreLevel?: string;
      };
      peerPilotModuleResult?: {
        label?: string;
        score?: number;
        scoreLevel?: string;
      };
      fileName?: string;
      fileUploadDate?: string;
      manuscriptId?: string;
    };

export function useChecklistState({
  checklist,
  documentId,
  initialDocumentProcess,
  editorialDesk = false,
  isPeerPilotReportReady = false,
  isImageCheckReportReady = false,
  isPlagiarismCheckReportReady = false,
  isV1 = false,
  on,
}: {
  checklist: Journal['checklist'];
  documentId: DocumentId;
  initialDocumentProcess: DocumentProcess | null;
  editorialDesk?: boolean;
  isPeerPilotReportReady?: boolean;
  isImageCheckReportReady?: boolean;
  isPlagiarismCheckReportReady?: boolean;
  isV1?: boolean;
  on: (
    event:
      | { name: 'loading' }
      | { name: 'error'; description: string }
      | {
          name: 'done';
          summaryResult: DocumentProcess['summaryResult'];
        },
  ) => void;
}) {
  const { refetchInterval, refreshRefetchInterval } = useRefetchInterval();
  const [checklistState, setChecklistState] = useState<ChecklistState>(
    initialDocumentProcess?.requestCompleted
      ? {
          name: 'done',
          checks: updateChecks(
            checklist.checks,
            initialDocumentProcess.completedChecks,
            'done',
            checklist.checks,
          ),
          summaryResult: initialDocumentProcess.summaryResult,
          languageSuggestions: initialDocumentProcess.languageSuggestions,
          peerPilotChecks: initialDocumentProcess?.peerPilotChecks,
          sectionWiseResult: initialDocumentProcess?.sectionWiseResult ?? {},
          subSectionWiseResult:
            initialDocumentProcess?.subSectionWiseResults ?? [],
          integrityCheckSummary:
            initialDocumentProcess?.integrityCheckSummary ?? '',
          languageAccessibilityScore:
            initialDocumentProcess?.languageAccessibilityScore,
          languageSummaryResult: initialDocumentProcess?.languageSummaryResult,
          technicalSummaryResult:
            initialDocumentProcess?.technicalSummaryResult,
          articleType: initialDocumentProcess?.articleType,
          selectedModules: initialDocumentProcess?.selectedModules,
          selectedModuleResults: (() => {
            if (
              initialDocumentProcess?.selectedModuleResults &&
              Object.values(initialDocumentProcess.selectedModuleResults).some(
                (v) => v !== undefined,
              )
            ) {
              return initialDocumentProcess.selectedModuleResults;
            }

            const docProcess = initialDocumentProcess as DocumentProcess & {
              plagiarismModuleResult?: {
                label?: string;
                score?: number;
                scoreLevel?: string;
              };
            };
            return {
              DETECT_AI:
                initialDocumentProcess?.detectAiModuleResult || undefined,
              PEER_PILOT:
                initialDocumentProcess?.peerPilotModuleResult || undefined,
              PLAGIARISM_CHECK: docProcess?.plagiarismModuleResult || undefined,
              IMAGE_CHECK: undefined, // Will be populated when IMAGE_CHECK data becomes available
            };
          })(),
          smartScreenModuleResult:
            initialDocumentProcess?.smartScreenModuleResult,
          detectAiModuleResult: initialDocumentProcess?.detectAiModuleResult,
          peerPilotModuleResult: initialDocumentProcess?.peerPilotModuleResult,
          fileName: initialDocumentProcess?.fileName,
          manuscriptId: initialDocumentProcess?.manuscriptId,
        }
      : {
          name: 'loading',
          checks: markAllChecksPending(checklist.checks),
          selectedModules: initialDocumentProcess?.selectedModules,
          fileName: initialDocumentProcess?.fileName,
          articleType: initialDocumentProcess?.articleType,
          fileUploadDate: initialDocumentProcess?.fileUploadDate,
          manuscriptId: initialDocumentProcess?.manuscriptId,
        },
  );
  const shouldStartFetching =
    checklistState.name === 'loading' ||
    isPeerPilotReportReady ||
    isImageCheckReportReady ||
    isPlagiarismCheckReportReady;

  const rum = useRum();
  const { timeout } = useTimeout({
    delay: editorialDesk ? 1800000 : 600000,
    onTimedOut: () => {
      rum.pushError(
        new Error(
          'Document processing failed to complete within the expected time',
        ),
        {
          type: 'Generic Error',
          context: {
            errorName:
              'Document processing failed to complete within the expected time',
            documentId: documentId,
          },
        },
      );
      on({ name: 'error', description: 'Check processing failed' });
      setChecklistState({
        name: 'error',
        checks: markRemainingChecksError(checklistState.checks),
        selectedModules: checklistState.selectedModules,
        fileName: checklistState.fileName,
        articleType: checklistState.articleType,
        fileUploadDate: checklistState.fileUploadDate,
        manuscriptId: checklistState.manuscriptId,
        retry: () => {},
      });
    },
  });

  useEffect(() => {
    if (!initialDocumentProcess?.requestCompleted) {
      on({ name: 'loading' });
      timeout.start();
    }
  }, [initialDocumentProcess?.requestCompleted, on, timeout]);

  useQuery<DocumentProcess, HttpError>({
    queryKey: [
      'getDocumentProcess',
      documentId,
      checklist.groups,
      isV1,
      isPeerPilotReportReady,
      isImageCheckReportReady,
      isPlagiarismCheckReportReady,
    ],
    queryFn: () =>
      getDelayedDocumentProcess(documentId, checklist.groups, isV1),
    enabled: shouldStartFetching,
    onSuccess: (data) => {
      if (data.requestCompleted) {
        setChecklistState({
          name: 'done',
          checks: updateChecks(
            checklistState.checks,
            data.completedChecks,
            'done',
            checklist.checks,
          ),
          summaryResult: data.summaryResult,
          languageSuggestions: data.languageSuggestions,
          peerPilotChecks: data?.peerPilotChecks,
          sectionWiseResult: data?.sectionWiseResult ?? {},
          subSectionWiseResult: data?.subSectionWiseResults ?? [],
          integrityCheckSummary: data?.integrityCheckSummary ?? '',
          languageAccessibilityScore: data?.languageAccessibilityScore,
          languageSummaryResult: data?.languageSummaryResult,
          technicalSummaryResult: data?.technicalSummaryResult,
          articleType: data?.articleType,
          selectedModules: data?.selectedModules,
          selectedModuleResults: (() => {
            if (
              data?.selectedModuleResults &&
              Object.values(data.selectedModuleResults).some(
                (v) => v !== undefined,
              )
            ) {
              return data.selectedModuleResults;
            }

            const docData = data as DocumentProcess & {
              plagiarismModuleResult?: {
                label?: string;
                score?: number;
                scoreLevel?: string;
              };
            };
            return {
              DETECT_AI: data?.detectAiModuleResult || undefined,
              PEER_PILOT: data?.peerPilotModuleResult || undefined,
              PLAGIARISM_CHECK: docData?.plagiarismModuleResult || undefined,
              IMAGE_CHECK: undefined, // Will be populated when IMAGE_CHECK data becomes available
            };
          })(),
          smartScreenModuleResult: data?.smartScreenModuleResult,
          detectAiModuleResult: data?.detectAiModuleResult,
          peerPilotModuleResult: data?.peerPilotModuleResult,
          fileName: data?.fileName,
          fileUploadDate: data?.fileUploadDate,
          manuscriptId: data?.manuscriptId,
        });
        on({
          name: 'done',
          summaryResult: data.summaryResult,
        });
        timeout.stop();
      } else {
        refreshRefetchInterval();
        setChecklistState({
          name: 'loading',
          checks: updateChecks(
            checklistState.checks,
            data.completedChecks,
            'loading',
            checklist.checks,
          ),
          peerPilotChecks: data?.peerPilotChecks,
          selectedModules: data?.selectedModules,
          fileName: data?.fileName,
          articleType: data?.articleType,
          fileUploadDate: data?.fileUploadDate,
          manuscriptId: data?.manuscriptId,
        });
      }
    },
    retry: 2,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10000),
    onError: (error) => {
      rum.pushError(
        new Error(`Document processing failed - ${JSON.stringify(error.json)}`),
        {
          type: 'Generic Error',
          context: {
            errorName: error?.message ?? '',
            documentId: documentId,
          },
        },
      );
      on({ name: 'error', description: error?.message ?? '' });
      setChecklistState({
        name: 'error',
        checks: markRemainingChecksError(checklistState.checks),
        selectedModules: checklistState.selectedModules,
        fileName: checklistState.fileName,
        articleType: checklistState.articleType,
        fileUploadDate: checklistState.fileUploadDate,
        manuscriptId: checklistState.manuscriptId,
        retry: () => {},
      });
      timeout.stop();
    },
    refetchInterval:
      checklistState.name === 'loading' ? refetchInterval : false,
  });

  return checklistState;
}

function markAllChecksPending(checks: Checks): Checks {
  return Object.fromEntries(
    Object.entries(checks).map(([checkKey, check]) => [
      checkKey,
      { ...check, state: 'pending' },
    ]),
  );
}

function markRemainingChecksError(checks: Checks): Checks {
  return Object.fromEntries(
    Object.entries(checks).map(([checkKey, check]) => [
      checkKey,
      check.state === 'pending' ? { ...check, state: 'error' } : check,
    ]),
  );
}

function updateChecks(
  checks: Checks,
  completedChecks: CheckState[],
  status: 'loading' | 'done',
  journalChecks: Checks,
): Checks {
  const updatedChecks = { ...checks };

  const langFilterCheck = Object.values(checks).filter(
    (item) => item.grouping === 'language',
  );
  const skippedLangCheck = langFilterCheck.filter(
    (lcheck) =>
      !completedChecks.some((cCheck) => lcheck.checkKey === cCheck.checkKey),
  );
  const completedCheckWithSkipCheck = [...completedChecks];
  if (status === 'done') {
    skippedLangCheck.forEach((item) => {
      completedCheckWithSkipCheck.push({
        checkKey: item.checkKey,
        description: {
          text: item.content.text,
        },
        label: item.label,
        result: 'successful',
      });
    });
  }
  completedCheckWithSkipCheck.forEach((completedCheck) => {
    let checkToUpdate = checks[completedCheck.checkKey];
    if (!checkToUpdate && completedCheck.checkKey === 'ai_detector_check') {
      checkToUpdate = checks['ai_detector_versioned_check'];
    }
    if (checkToUpdate) {
      const checksLabel = journalChecks[completedCheck.checkKey]?.label ?? '';
      updatedChecks[checkToUpdate.checkKey] = {
        ...checkToUpdate,
        content: {
          text: completedCheck.description.text,
          links: completedCheck.description.links ?? [],
        },
        label: completedCheck.label,
        checksLabel: checksLabel,
        state: completedCheck.result,
        groupSubSection: completedCheck.groupSubSection ?? '',
        additionalDescription: completedCheck.additionalDescription,
        issues:
          typeof completedCheck.issues === 'number'
            ? completedCheck.issues
            : undefined,
        category: completedCheck.category ?? '',
      };
    }
  });

  return updatedChecks;
}

function useRefetchInterval() {
  const [refetchInterval, setRefetchInterval] = useState(
    randomPollIntervalGeneration(6000),
  );

  return {
    refetchInterval,
    refreshRefetchInterval: () => {
      setRefetchInterval(randomPollIntervalGeneration(6000));
    },
  };
}

function randomPollIntervalGeneration(base: number) {
  const randomPollInterval = getRandomPollInterval(base);

  return randomPollInterval < 3000 ? 3000 : randomPollInterval;
}
